import { useMemo, useState } from "react";
import type { CalendarEvent, Schedule } from "@rma-orbit/shared";

import { api } from "../api/client.js";
import { navigate } from "../App.js";
import { Busy, ErrorNotice, Notice, useAsync } from "../lib/ui.js";

/**
 * 달력 (harness/API-SPEC.md §2-5).
 *
 * 한 달을 격자로 보여 준다. 칸 안의 점은 그날의 **사건**(입고·SECUI행·협력사행·교체 — 케이스에서
 * 읽어 온다)과 **일정**(사람이 적은 것)이다. 날짜를 누르면 오른쪽에 그날의 목록과 일정 입력이 뜬다.
 * 지연 경고는 두지 않는다(C5) — 날짜와 사실만 보여 준다.
 */

const KIND_LABEL: Record<CalendarEvent["kind"], string> = {
  received: "입고",
  shipped_vendor: "SECUI행",
  shipped_owner: "협력사행",
  swapped: "교체",
};

function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function kstToday(): Date {
  const s = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Seoul" }).format(new Date());
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y!, m! - 1, d!);
}

export function Calendar({ can }: { can: (p: string) => boolean }) {
  const today = useMemo(kstToday, []);
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [picked, setPicked] = useState<string>(ymd(today));

  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const last = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
  // 격자는 일요일부터 6주
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - first.getDay());
  const gridEnd = new Date(gridStart);
  gridEnd.setDate(gridStart.getDate() + 41);

  const from = ymd(gridStart);
  const to = ymd(gridEnd);
  const data = useAsync(() => api.calendar(from, to), [from, to]);

  const byDate = useMemo(() => {
    const map = new Map<string, { events: CalendarEvent[]; items: Schedule[] }>();
    for (const e of data.data?.events ?? []) {
      const slot = map.get(e.on_date) ?? { events: [], items: [] };
      slot.events.push(e);
      map.set(e.on_date, slot);
    }
    for (const s of data.data?.items ?? []) {
      const slot = map.get(s.on_date) ?? { events: [], items: [] };
      slot.items.push(s);
      map.set(s.on_date, slot);
    }
    return map;
  }, [data.data]);

  const days: Date[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    days.push(d);
  }

  const monthLabel = `${cursor.getFullYear()}년 ${cursor.getMonth() + 1}월`;
  const monthEvents = (data.data?.events ?? []).filter((e) => e.on_date >= ymd(first) && e.on_date <= ymd(last));
  const counts = {
    received: monthEvents.filter((e) => e.kind === "received").length,
    vendor: monthEvents.filter((e) => e.kind === "shipped_vendor").length,
    owner: monthEvents.filter((e) => e.kind === "shipped_owner").length,
    items: (data.data?.items ?? []).filter((s) => s.on_date >= ymd(first) && s.on_date <= ymd(last)).length,
  };

  return (
    <>
      <div className="row" style={{ alignItems: "flex-end", marginBottom: 14 }}>
        <div className="grow">
          <h1>달력</h1>
          <p className="page-desc" style={{ margin: 0 }}>
            입고·출고·교체는 케이스에서 읽어 오고, 일정은 여기서 적습니다. 지연 경고는 두지 않습니다.
          </p>
        </div>
        <div className="row tight">
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}>‹</button>
          <button onClick={() => { setCursor(new Date(today.getFullYear(), today.getMonth(), 1)); setPicked(ymd(today)); }}>오늘</button>
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}>›</button>
        </div>
      </div>

      <ErrorNotice error={data.error} />

      <div className="row" style={{ alignItems: "stretch" }}>
        <div className="card stat"><div className="stat-label">입고</div><div className="stat-value">{counts.received}</div></div>
        <div className="card stat"><div className="stat-label">SECUI행</div><div className="stat-value">{counts.vendor}</div></div>
        <div className="card stat"><div className="stat-label">협력사행</div><div className="stat-value">{counts.owner}</div></div>
        <div className="card stat accent"><div className="stat-label">일정</div><div className="stat-value">{counts.items}</div></div>
      </div>

      <div className="cal-layout">
        <div className="card cal-card">
          <h2>
            {monthLabel}
            <span className="sub">
              <span className="cal-key received" /> 입고 <span className="cal-key vendor" /> SECUI행{" "}
              <span className="cal-key owner" /> 협력사행 <span className="cal-key swapped" /> 교체{" "}
              <span className="cal-key item" /> 일정
            </span>
          </h2>
          {data.loading && !data.data ? <Busy /> : null}
          <div className="cal-grid">
            {["일", "월", "화", "수", "목", "금", "토"].map((w, i) => (
              <div key={w} className={`cal-dow ${i === 0 ? "sun" : i === 6 ? "sat" : ""}`}>{w}</div>
            ))}
            {days.map((d) => {
              const key = ymd(d);
              const slot = byDate.get(key);
              const other = d.getMonth() !== cursor.getMonth();
              const isToday = key === ymd(today);
              return (
                <button
                  key={key}
                  className={`cal-cell ${other ? "other" : ""} ${isToday ? "today" : ""} ${picked === key ? "picked" : ""} ${d.getDay() === 0 ? "sun" : d.getDay() === 6 ? "sat" : ""}`}
                  onClick={() => {
                    setPicked(key);
                    // 좁은 화면에서는 오른쪽 패널이 아래로 내려간다 — 고른 날짜의 패널로 바로 데려간다
                    if (window.innerWidth < 1100) {
                      window.setTimeout(() => document.querySelector(".cal-side")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
                    }
                  }}
                >
                  <span className="cal-day">{d.getDate()}</span>
                  {slot && (
                    <span className="cal-dots">
                      {slot.events.slice(0, 6).map((e, i) => (
                        <i key={i} className={`cal-dot ${e.kind === "received" ? "received" : e.kind === "shipped_vendor" ? "vendor" : e.kind === "shipped_owner" ? "owner" : "swapped"}`} />
                      ))}
                      {slot.items.slice(0, 3).map((s) => (
                        <i key={`s${s.id}`} className="cal-dot item" />
                      ))}
                    </span>
                  )}
                  {slot && slot.items.length > 0 && (
                    <span className="cal-title">{slot.items[0]!.title}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <DayPanel
          date={picked}
          slot={byDate.get(picked) ?? { events: [], items: [] }}
          canWrite={can("case.write")}
          onChanged={data.reload}
        />
      </div>
    </>
  );
}

function DayPanel({
  date,
  slot,
  canWrite,
  onChanged,
}: {
  date: string;
  slot: { events: CalendarEvent[]; items: Schedule[] };
  canWrite: boolean;
  onChanged: () => void;
}) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [caseNo, setCaseNo] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function add() {
    setError(null);
    if (!title.trim()) return;
    setBusy(true);
    try {
      let caseId: number | undefined;
      if (caseNo.trim()) {
        const found = await api.cases({ limit: 200 });
        const hit = found.items.find((c) => c.case_no === caseNo.trim().toUpperCase());
        if (!hit) throw new Error(`케이스 ${caseNo.trim()} 을 찾지 못했습니다.`);
        caseId = hit.id;
      }
      await api.createSchedule({ on_date: date, title: title.trim(), body: body.trim() || undefined, ...(caseId ? { case_id: caseId } : {}) });
      setTitle("");
      setBody("");
      setCaseNo("");
      onChanged();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: number) {
    if (!window.confirm("이 일정을 지울까요?")) return;
    setError(null);
    try {
      await api.deleteSchedule(id);
      onChanged();
    } catch (err) {
      setError(err);
    }
  }

  const pretty = new Intl.DateTimeFormat("ko-KR", { month: "long", day: "numeric", weekday: "short" }).format(new Date(`${date}T00:00:00`));

  return (
    <div className="card cal-side">
      <h2>
        {pretty}
        <span className="sub">사건 {slot.events.length} · 일정 {slot.items.length}</span>
      </h2>
      <ErrorNotice error={error} />

      {canWrite && (
        <div className="cal-add">
          <label className="field">
            일정 제목
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="예: 나래넷츠 교체품 회수 방문" />
          </label>
          <label className="field">
            메모 (선택)
            <textarea value={body} onChange={(e) => setBody(e.target.value)} style={{ minHeight: 56 }} />
          </label>
          <label className="field">
            케이스 번호 (선택)
            <input value={caseNo} onChange={(e) => setCaseNo(e.target.value)} placeholder="RMA-2026-0001" />
          </label>
          <button className="primary" disabled={busy || !title.trim()} onClick={() => void add()}>
            {busy ? "저장 중…" : "이날에 일정 추가"}
          </button>
        </div>
      )}
      {!canWrite && slot.items.length === 0 && (
        <Notice kind="info">일정을 적으려면 케이스 쓰기 권한이 필요합니다.</Notice>
      )}

      {slot.events.length === 0 && slot.items.length === 0 && (
        <p className="muted" style={{ margin: "0 0 10px" }}>이날은 기록이 없습니다.</p>
      )}

      <div className="cal-day-lists">
      {slot.events.length > 0 && (
        <ul className="cal-list">
          {slot.events.map((e, i) => (
            <li key={i}>
              <span className={`badge kind-${e.kind}`}>{KIND_LABEL[e.kind]}</span>
              <div className="cal-ev">
                <div className="cal-ev-top">
                  <a
                    href={`#/cases/${e.case_id}`}
                    onClick={(ev) => { ev.preventDefault(); navigate(`#/cases/${e.case_id}`); }}
                  >
                    {e.case_no}
                  </a>
                  {e.company && <span className="muted cal-ev-co" title={e.company}>{e.company}</span>}
                </div>
                <div className="mono muted cal-ev-serial">{e.serial_norm}</div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {slot.items.length > 0 && (
        <ul className="cal-list">
          {slot.items.map((s) => (
            <li key={s.id} className="item">
              <span className="badge kind-item">일정</span>
              <div className="grow">
                <strong>{s.title}</strong>
                {s.case_no && (
                  <>
                    {" "}
                    <a href={`#/cases/${s.case_id}`} onClick={(ev) => { ev.preventDefault(); navigate(`#/cases/${s.case_id}`); }}>{s.case_no}</a>
                  </>
                )}
                {s.body && <div className="muted" style={{ fontSize: 12, whiteSpace: "pre-wrap" }}>{s.body}</div>}
              </div>
              {canWrite && (
                <button className="ghost" title="지우기" onClick={() => void remove(s.id)}>×</button>
              )}
            </li>
          ))}
        </ul>
      )}
      </div>
    </div>
  );
}
